import {
  Home, Search, Car, MessageCircle, ClipboardList, ShieldCheck,
  KeyRound, Clock3, RefreshCw, CheckCircle2, Star, RotateCcw, Sparkles,
  AlertTriangle, Wrench, Bell, ArrowRightLeft, Send, PartyPopper,
} from "lucide-react";

/**
 * The full real renter journey, faithfully, not compressed to a round
 * number. Each step maps to either a real ConfirmPH screen (rendered with
 * local demo data, fully interactive, never navigating the real browser) or
 * an explicitly-labeled simulated moment (chat, staff-side booking capture,
 * time-based notifications), never an invented look-alike of real UI.
 *
 * `screen` selects the component RenterWalkthroughStage renders.
 * `spotlight` names the data-testid(s) to guide the visitor to on this
 * step. Steps with multiple guided sub-phases (business page, chat,
 * confirmation) resolve their spotlight target dynamically in
 * RenterWalkthroughStage's resolveSpotlightTarget: the strings here are
 * either the single target or a small per-phase map.
 *
 * Design note: the real vehicle-detail view and the real contact card are
 * both part of the SAME real business page (a modal + an on-page card, not
 * separate pages), so "vehicle details" and "contact" are guided phases of
 * the "business" step rather than invented standalone pages, matching how
 * the real product actually presents them.
 */
export const RENTER_STEPS = [
  {
    key: "home",
    screen: "home",
    title: "Start on the homepage",
    eyebrow: "confirmph.app",
    icon: Home,
    description: "This is the real ConfirmPH homepage. Renters start here and use the header to discover rentals.",
    points: ["Real header, live", "Nothing here is a screenshot", "Tap the highlighted link to continue"],
    spotlight: {
      desktop: "nav-discover-rentals",
      desktopTooltip: "Click here to find listed rentals.",
      phone: "public-mobile-menu-toggle",
      phoneTooltip: "Open the menu.",
      phoneDrawer: "drawer-nav-discover-rentals",
      phoneDrawerTooltip: "Tap Discover Rentals.",
    },
  },
  {
    key: "discover",
    screen: "discover",
    title: "Find a rental",
    eyebrow: "Discover Rentals",
    icon: Search,
    description: "Browse listed rental businesses: the real listing page, showing the one real, intentional demo business.",
    points: ["Real listings UI", "Compare vehicles and ratings", "Tap the business to open its profile"],
    spotlight: { both: "demo-rental-card", tooltip: "Open this rental business." },
  },
  {
    key: "business",
    screen: "business",
    title: "Choose a vehicle",
    eyebrow: "Rental business profile",
    icon: Car,
    description: "The real business page: vehicles, contact info, reviews. Open the vehicle for full details, then see how you'd reach this business.",
    points: ["Tap the highlighted vehicle to open its full details", "Close the details, then tap Continue to see how you'd contact them", "Real vehicle card + real contact card, same as production"],
    spotlight: {
      vehicle: "vehicle-tooltip", // resolved dynamically per-vehicle id
      vehicleTooltip: "Open this vehicle's full details.",
      modalClose: "pvdm-close",
      modalCloseTooltip: "Close vehicle details.",
      continue: "walkthrough-business-continue",
      continueTooltip: "This is how you'd contact them: continue when ready.",
    },
  },
  {
    key: "chat-inquiry",
    screen: "chat",
    chatPhase: "inquiry",
    title: "The conversation happens",
    eyebrow: "Messenger · simulated",
    icon: MessageCircle,
    description: "This part happens outside ConfirmPH, so it's simulated here: an honest recreation of a real Messenger exchange.",
    points: ["Watch it play out automatically", "No Next needed between messages", "The business will record this and send your confirmation"],
    spotlight: { both: "walkthrough-chat-continue", tooltip: "Continue." },
  },
  {
    key: "business-booking",
    screen: "business-booking",
    title: "The business records your booking",
    eyebrow: "ConfirmPH · staff side",
    icon: ClipboardList,
    description: "On their end, the business is using ConfirmPH right now: recording what you agreed on, then preparing your confirmation. Tap Continue to follow along.",
    points: ["Watch the draft become a booking awaiting your confirmation", "A confirmation link gets generated for you", "Tap Continue below when you're ready"],
    spotlight: { both: "walkthrough-business-booking-continue", tooltip: "Continue." },
  },
  {
    key: "chat-confirmation-link",
    screen: "chat",
    chatPhase: "confirmation",
    title: "Your confirmation link arrives",
    eyebrow: "Messenger · simulated",
    icon: MessageCircle,
    description: "Same conversation as before: nothing replays. The business just sent your real ConfirmPH confirmation link.",
    points: ["Earlier messages are already there", "Only this new message animates in", "Tap the link to open your confirmation"],
    spotlight: { both: "walkthrough-chat-link-card", tooltip: "Open your confirmation." },
  },
  {
    key: "confirmation",
    screen: "confirmation",
    title: "Review and confirm your booking",
    eyebrow: "Awaiting Customer Confirmation",
    icon: ShieldCheck,
    description: "The real confirmation page: vehicle, dates, price, and any existing damage recorded on the vehicle before you confirm.",
    points: ["Check the vehicle first", "Real existing-damage sheet", "Confirm to move the booking forward"],
    spotlight: { both: "confirm-booking-btn", tooltip: "Confirm this booking" },
  },
  {
    key: "confirmed",
    screen: "confirmed",
    title: "Booking confirmed",
    eyebrow: "Confirmed",
    icon: CheckCircle2,
    description: "The real My Booking page: this is what a confirmed customer actually sees, including the confirmation-version history. There's nothing to click here; when you've had a look, use Next below to continue.",
    points: ["Real status badge, real confirmation history", "Business contact stays visible", "Nothing to click: tap Next below when ready"],
    spotlight: { both: "confirmation-history-card", tooltip: "Your confirmation record" },
  },
  {
    key: "pickup-reminder",
    screen: "notification",
    title: "Pickup reminder",
    eyebrow: "Simulated notification",
    icon: Clock3,
    description: "As pickup day approaches, ConfirmPH can remind you. This reminder is simulated: the timing is real, the notification text is not sent right now.",
    points: ["A day-before pickup reminder", "Business contact included", "Continue to pickup"],
    spotlight: { both: "walkthrough-notification-continue", tooltip: "View booking." },
    notification: { title: "Reminder: pickup tomorrow at 9:00 AM", body: "Bring a valid ID. Contact the business if your plans change." },
  },
  {
    key: "pickup-receive",
    screen: "pickup-receive",
    title: "Receiving the vehicle",
    eyebrow: "Pickup",
    icon: Car,
    description: "At pickup, compare the actual vehicle against what was recorded before you accept it: this is the one habit that prevents disputes later.",
    points: ["Check existing damage matches what's recorded", "Check the odometer and fuel/charge level", "Confirm keys and included items"],
    spotlight: { both: "walkthrough-pickup-continue", tooltip: "I've checked the vehicle." },
  },
  {
    key: "ongoing",
    screen: "ongoing",
    title: "Rental ongoing",
    eyebrow: "Ongoing",
    icon: Car,
    description: "The same real My Booking page: now showing the Ongoing status, exactly as a renter would see it mid-rental. Nothing to click here; tap Next below when you're ready to continue.",
    points: ["Real status badge", "Business contact remains visible", "Nothing to click: tap Next below when ready"],
    spotlight: { both: "portal-bd-status-explainer", tooltip: null },
  },
  {
    key: "return-transition",
    screen: "notification",
    title: "Vehicle returned",
    eyebrow: "Simulated notification",
    icon: RefreshCw,
    description: "Return and inspection happen on the business's side: mileage, fuel, and condition get recorded there. The booking simply moves to Completed once they finish.",
    points: ["No separate renter-facing inspection screen exists today", "The business handles the return checks", "Continue to see the completed booking"],
    spotlight: { both: "walkthrough-notification-continue", tooltip: "Continue." },
    notification: { title: "Vehicle returned: finishing final checks", body: "The business is wrapping up the return. You'll see this booking marked Completed shortly." },
  },
  {
    key: "completed",
    screen: "completed",
    title: "Rental completed",
    eyebrow: "Completed",
    icon: KeyRound,
    description: "The real My Booking page again: Completed status. The business will invite you to review before you can book again. Nothing to click here; tap Next below to see your review invite arrive.",
    points: ["Real status badge: rental is now fully completed", "Next: your review invite arrives", "Nothing to click: tap Next below when ready"],
    spotlight: null,
  },
  {
    key: "chat-review-link",
    screen: "chat",
    chatPhase: "review",
    title: "Your review invite arrives",
    eyebrow: "Messenger · simulated",
    icon: MessageCircle,
    description: "Same conversation, still no replay: the business just sent your review link now that the rental is complete.",
    points: ["All earlier messages are already there", "Only this new message animates in", "Tap the link to leave your review"],
    spotlight: { both: "walkthrough-chat-link-card", tooltip: "Open your review." },
  },
  {
    key: "review",
    screen: "review",
    title: "Leave a review",
    eyebrow: "Simulated invite → real review form",
    icon: Star,
    description: "ConfirmPH emails a one-time review link after a completed booking. That email is simulated here; the form itself is the real review page.",
    points: ["Real star-rating form", "Real character-count validation", "Submitting here doesn't post anything real"],
    spotlight: { both: "review-submit-btn", tooltip: "Submit your review" },
  },
  {
    key: "review-shown",
    screen: "business",
    title: "Your review, on the business page",
    eyebrow: "Rental business profile",
    icon: Sparkles,
    description: "Back on the real business page: this is where your review actually shows up for future renters to see. Nothing to click here; tap Next below to continue.",
    points: ["Real reviews section: your demo review appears here", "Nothing was posted to the real database", "Nothing to click: tap Next below when ready"],
    spotlight: { reviewPlaceholder: true, tooltip: "Your new review." },
  },
  {
    key: "book-again",
    screen: "book-again",
    title: "Book it again",
    eyebrow: "My Bookings",
    icon: RotateCcw,
    description: "Book Again carries your details forward to a new booking with the same business: this is the real link, shown here without leaving the walkthrough.",
    points: ["Real prefill: name, email, phone, vehicle", "Opens the business's real booking page in real life", "You've now seen the complete renter flow"],
    spotlight: { both: "portal-bd-book-again", tooltip: "Book again with the same details" },
  },
  {
    key: "outro",
    screen: "outro",
    title: "That's the full renter journey",
    eyebrow: "ConfirmPH",
    icon: Sparkles,
    description: "From finding a rental to leaving a review: all real ConfirmPH screens, nothing left this page.",
    points: ["Real screens throughout", "No real bookings were created", "Try it yourself on the real site"],
    spotlight: null,
  },

  // The issue branch is a customer journey, not an operations tutorial.
  // It deliberately opens OUTSIDE the app entirely: a phone lock screen with
  // a notification, not an already-open booking page. Anna has not opened
  // ConfirmPH yet when this branch starts; see RenterWalkthroughStage's
  // LockScreenNotification and ProductWalkthrough's per-mode defaultDevice
  // (the issue mode boots into phone view for exactly this reason).
  { key: "issue-notification", screen: "notification", lockScreen: true, title: "A booking notice arrives", eyebrow: "1 · Phone notification", icon: Bell,
    description: "This is the first thing Anna sees: her phone, locked, before she has opened ConfirmPH at all. A customer-safe notification says there may be a problem. Tap it to deep-link straight into the affected booking.",
    points: ["No private accident details", "No replacement guarantee", "Tap the notification to open the booking"],
    spotlight: { both: "walkthrough-notification-continue", tooltip: "Open the booking" },
    notification: { title: "There may be an issue with your upcoming booking", body: "Open your booking and contact Traveler Car Rental for more information." } },
  { key: "issue-notice", screen: "issue-notice", title: "Understand the problem", eyebrow: "2 · My Booking", icon: AlertTriangle,
    description: "Tapping the notification deep-links straight here: Anna's real My Booking page, now showing a clear warning and only the renter-safe notice, never internal incident detail.",
    points: ["Internal incident details stay private", "A replacement is not promised", "Contact the booking provider"],
    spotlight: { both: "portal-bd-contact-business", tooltip: "Contact Booking Provider" } },
  { key: "issue-chat", screen: "issue-chat", title: "Ask the rental company", eyebrow: "3 · Messenger · simulated", icon: MessageCircle,
    description: "The conversation happens outside ConfirmPH. Traveler Car Rental explains the vehicle was involved in an incident and that a replacement is being arranged.",
    points: ["Short, natural conversation", "No guarantee until availability is checked", "Continue after the reply"],
    spotlight: { both: "walkthrough-chat-continue", tooltip: "Continue" } },
  { key: "issue-business-fix", screen: "issue-business-fix", title: "The business resolves the warning", eyebrow: "4 · Behind the scenes", icon: ArrowRightLeft,
    description: "Switching to the business side: briefly see the real Warning Center and real Reassign Vehicle modal staff use. Notify Anna, then choose the suitable available replacement.",
    points: ["Real warning component", "Real replacement candidate checks", "Material change requires reconfirmation"],
    spotlight: { both: "warning-notify-demo-warning-1", tooltip: "Notify the renter" } },
  { key: "issue-updated-notification", screen: "notification", lockScreen: true, title: "The updated booking arrives", eyebrow: "5 · Phone notification", icon: Send,
    description: "Back to Anna's phone, locked again, outside the app: a second notification asks her to review the material change.",
    points: ["The change is not silently accepted", "The old confirmation stays on record", "Tap to review"],
    spotlight: { both: "walkthrough-notification-continue", tooltip: "Review the changes" },
    notification: { title: "Traveler Car Rental updated your booking", body: "Review the changed vehicle and confirm whether you accept it." } },
  { key: "issue-change-review", screen: "issue-change-review", title: "Review what changed", eyebrow: "6 · Updated confirmation", icon: ShieldCheck,
    description: "Tapping the second notification deep-links here: the actual customer reconfirmation page, showing the original Kia Rio beside the Toyota Montero replacement.",
    points: ["Old and new terms are visible", "Nothing is accepted automatically", "Review the comparison first"],
    spotlight: { both: "walkthrough-change-review-continue", tooltip: "Continue to Confirm Changes" } },
  { key: "renter-reviews", screen: "renter-reviews", title: "Confirm the changes", eyebrow: "7 · Explicit acceptance", icon: ShieldCheck,
    description: "Anna now uses the actual Accept Changes action. This explicit approval is what finalizes the replacement.",
    points: ["Real reconfirmation control", "Customer choice is recorded", "No silent material change"],
    spotlight: { both: "pending-change-accept-btn", tooltip: "Accept Changes" } },
  {
    key: "reconfirmed",
    screen: "reconfirmed",
    title: "Booking reconfirmed",
    eyebrow: "Confirmed: updated",
    icon: PartyPopper,
    description: "ConfirmPH didn't prevent the vehicle problem: nothing can promise that. What it did: made the change visible, kept a clear record, and got your agreement before anything was final.",
    points: ["Real status badge, real confirmation history", "Two confirmation versions on record, not one silently overwritten", "That record is the actual product"],
    spotlight: null,
  },
  {
    key: "issue-outro",
    screen: "outro",
    title: "That's the incident walkthrough",
    eyebrow: "ConfirmPH",
    icon: Sparkles,
    description: "From the first notification to a reconfirmed booking: all real ConfirmPH screens, nothing left this page.",
    points: ["Real incident + replacement workflow", "No real bookings were created", "Try it yourself on the real site"],
    spotlight: null,
  },
];
